import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import FileUploadManager, {
  FileUploadManagerRef,
} from "../../../../components/forms/fileUploadManager";
import TextInput from "../../../../components/forms/textInput";
import MilestoneHeader from "../milestoneHeader";
import styles from "./styles.module.scss";
import MilestoneFormNavButton from "../milestoneFormNavButton";
import { ReactComponent as InfoIcon } from "../../../../assets/images/svg/infoIcon.svg";
import {
  getOrganization,
  getOrganizationPaymentMethods,
} from "../../../../setup/api/services/organization";
import { useOrganizationCode } from "../../../../setup/util/extractOrganizationCode";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import {
  EPaymentType,
  EPaymentBrand,
} from "../../../../setup/api/services/contract/models";
import Modal from "../../../../components/common/Modal";
import TiersInformation from "../../settings/components/TiersInformation";
import { IOrganizations } from "../../../../setup/types/Organisation";
import OutlinedButton from "../../../../components/common/OutlinedButton";
import { useParams } from "react-router-dom";
import { useSourceDestinationEnabled } from "../../../../hooks/useSourceDestinationEnabled";

interface IStepTwoProps {
  currentStep: number;
  fileUploadref: React.RefObject<FileUploadManagerRef>;
  handleSaveDraft: () => void;
  handleNext: () => void;
  handlePrev: (() => void) | undefined;
  loading: boolean;
}

const StepTwo = ({
  currentStep,
  fileUploadref,
  handleSaveDraft,
  handlePrev,
  handleNext,
  loading,
}: IStepTwoProps) => {
  const { t } = useTranslation();
  const { milestoneId, complexId } = useParams();
  const isEditMode = !!complexId && !!milestoneId;
  const {
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors, touchedFields },
  } = useFormContext();

  const userToken = AuthConfig.getToken();
  const { isSourceDestinationEnabled } = useSourceDestinationEnabled();

  const [isUploading, setIsUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [organizationMethods, setOrganizationMethods] =
    useState<Partial<Record<EPaymentType, EPaymentBrand[]>>>();
  const [organization, setOrganization] = useState<IOrganizations | undefined>(
    undefined
  );

  const organizationCode = useOrganizationCode(userToken) || undefined;

  const fetchOrganization = useCallback(async () => {
    if (!organizationCode) return;
    const response = await getOrganization(organizationCode as string);
    setOrganization(response.data);
  }, [organizationCode]);

  useEffect(() => {
    if (organizationCode) {
      fetchOrganization();
    }
  }, [fetchOrganization, organizationCode]);

  const fetchOrganizationPaymentMethods = useCallback(async () => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");

    if (
      !organizationCode ||
      Object.values(organizationMethods || {}).some(
        (methods) => Array.isArray(methods) && methods.length > 0
      )
    )
      return;

    const response = await getOrganizationPaymentMethods(
      organizationCode as string
    );
    setOrganizationMethods({
      ...response.data.paymentMethods,
      [EPaymentType.APPLE_PAY]: response?.data?.paymentMethods?.[
        EPaymentType.APPLE_PAY
      ]?.filter((brand) => brand !== EPaymentBrand.APPLE_PAY),
      [EPaymentType.MOYASAR]: response?.data?.paymentMethods?.[
        EPaymentType.MOYASAR
      ]?.filter((brand) => brand !== EPaymentBrand.APPLE_PAY),
      [EPaymentType.STCPAY]: response?.data?.paymentMethods?.[
        EPaymentType.STCPAY
      ]?.filter((brand) => brand !== EPaymentBrand.STCPAY),
    });

    if (!isEditMode) {
      const paymentMethodsPath = `milestones.${currentMilestoneIndex}.paymentMethods`;
      setValue(
        `${paymentMethodsPath}.cards`,
        (response.data.paymentMethods?.[EPaymentType.MOYASAR] || [])?.length > 0
          ? response.data.paymentMethods?.[EPaymentType.MOYASAR]?.filter(
              (method) => method !== EPaymentBrand.APPLE_PAY
            )
          : []
      );
      setValue(
        `${paymentMethodsPath}.apple_pay`,
        (response.data.paymentMethods?.[EPaymentType.APPLE_PAY] || [])?.length >
          0
          ? response.data.paymentMethods?.[EPaymentType.APPLE_PAY]?.filter(
              (method) => method !== EPaymentBrand.APPLE_PAY
            )
          : []
      );
      setValue(
        `${paymentMethodsPath}.tabby`,
        (response.data.paymentMethods?.[EPaymentType.TABBY] || [])?.length > 0
          ? response.data.paymentMethods?.[EPaymentType.TABBY]
          : []
      );
      setValue(
        `${paymentMethodsPath}.bank_transfer`,
        (
          response.data.paymentMethods?.[EPaymentType.MANUAL_BANK_TRANSFER] ||
          []
        )?.length > 0
          ? response.data.paymentMethods?.[EPaymentType.MANUAL_BANK_TRANSFER] // this will be Bank transfer not manual bank transfer
          : []
      );
      setValue(
        `${paymentMethodsPath}.stc_pay`,
        (response.data.paymentMethods?.[EPaymentType.STCPAY] || [])?.length > 0
          ? response.data.paymentMethods?.[EPaymentType.STCPAY]?.filter(
              (method) => method !== EPaymentBrand.STCPAY
            )
          : []
      );
    }
  }, [isEditMode, organizationCode, organizationMethods, setValue, watch]);

  useEffect(() => {
    fetchOrganizationPaymentMethods();
  }, [fetchOrganizationPaymentMethods]);

  useEffect(() => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");
    const titlePath = `milestones.${currentMilestoneIndex}.title`;
    const hasError = !!errors?.milestones?.[currentMilestoneIndex]?.title;
    const isTouched =
      !!touchedFields?.milestones?.[currentMilestoneIndex]?.title;

    // If error shows up before user touched the field, hide it silently
    if (hasError && !isTouched) {
      clearErrors(titlePath);
    }
  }, [errors, touchedFields, clearErrors, watch]);

  const PAYMENT_METHODS = [
    {
      id: "cards",
      label: {
        title: t("lbl_bank_cards"),
        value: EPaymentType.MOYASAR,
      },
      options: [
        { label: t("lbl_master_card"), value: EPaymentBrand.MASTER },
        { label: t("lbl_visa"), value: EPaymentBrand.VISA },
        { label: t("lbl_mada"), value: EPaymentBrand.MADA },
        { label: t("lbl_amex"), value: EPaymentBrand.AMEX },
      ],
    },
    {
      id: "tabby",
      label: { title: t("lbl_tabby"), value: EPaymentBrand.TABBY },
      options: [],
    },
    {
      id: "stc_pay",
      label: { title: t("lbl_stc_pay"), value: EPaymentBrand.STCPAY },
      options: [],
    },
    {
      id: "bank_transfer",
      label: {
        title: t("lbl_bank_transfer"),
        value: EPaymentBrand.BANK_TRANSFER,
      },
      options: [],
    },
  ];

  const toggleSelectAll = (group) => {
    const allSelected =
      watch(`milestones.${currentMilestoneIndex}.paymentMethods`)[group.id]
        ?.length ===
      group.options.filter((opt) =>
        organizationMethods?.[group.label.value]?.includes(opt.value)
      )?.length;

    setValue(
      `milestones.${currentMilestoneIndex}.paymentMethods.${group.id}`,
      allSelected
        ? []
        : group.options
            ?.filter((opt) =>
              organizationMethods?.[group.label.value]?.includes(opt.value)
            )
            ?.map((opt) => opt.value)
    );
  };

  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;
  const isDestination = !!watch(
    `milestones.${currentMilestoneIndex}.isDestination`
  );
  const isValid =
    !!watch(`milestones.${currentMilestoneIndex}.title`) &&
    !errors?.milestones?.[currentMilestoneIndex]?.title &&
    !isUploading &&
    (isDestination
      ? !!watch(`milestones.${currentMilestoneIndex}.sourceContractId`)?.trim()
      : !!watch(`milestones.${currentMilestoneIndex}.paymentMethods`) &&
        Object.values(
          watch(`milestones.${currentMilestoneIndex}.paymentMethods`)
        ).some((methods) => Array.isArray(methods) && methods.length > 0) &&
        !errors?.milestones?.[currentMilestoneIndex]?.paymentMethods);

  return (
    <div className={styles.stepTwo}>
      <MilestoneHeader
        complexContractTitle={watch("title")}
        currentStep={currentStep}
        buyerOnly={!!watch(`milestones.${currentMilestoneIndex}.buyerOnly`)}
      />
      <div className={styles.fullWidth}>
        <Controller
          name={`milestones.${currentMilestoneIndex}.title`}
          control={control}
          rules={{ required: true, max: 100, min: 5 }}
          render={({ field }) => (
            <TextInput
              {...field}
              label={t("lbl_transaction_title")}
              maxCharacters={100}
              required
              hint={t("lbl_title_max_100_chars")}
              placeholder={t("lbl_title_placeholder")}
              errorMessage={errors?.milestones?.[
                currentMilestoneIndex
              ]?.title?.message?.toString()}
            />
          )}
        />
      </div>

      {!isDestination && (
        <div className={styles.box} style={{ gap: "unset" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            <h3 className={styles.boxTitle}>{t("lbl_pick_payment_methods")}</h3>
            <InfoIcon
              style={{ cursor: "pointer" }}
              onClick={() => setModalOpen(true)}
            />
          </div>

          <Controller
            name={`milestones.${currentMilestoneIndex}.paymentMethods`}
            control={control}
            rules={{ required: true }}
            render={() => (
              <>
                {PAYMENT_METHODS.map((group, index) => (
                  <div
                    key={group.id}
                    style={{
                      borderBottom:
                        index === PAYMENT_METHODS.length - 1
                          ? ""
                          : "1px solid #E2E5EE",
                      padding: "18px 0",
                      width: "100%",
                    }}>
                    {group?.options?.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "flex-start",
                          gap: "8px",
                        }}>
                        {/* // select all */}
                        <input
                          className={styles.paymentCheckBox}
                          type="checkbox"
                          disabled={
                            !organizationMethods?.[group.label.value] ||
                            organizationMethods?.[group.label.value]?.length ===
                              0
                          }
                          checked={
                            watch(
                              `milestones.${currentMilestoneIndex}.paymentMethods.${group.id}`
                            )?.length ===
                              group.options.filter((opt) =>
                                organizationMethods?.[
                                  group.label.value
                                ]?.includes(opt.value)
                              )?.length &&
                            watch(
                              `milestones.${currentMilestoneIndex}.paymentMethods.${group.id}`
                            )?.length !== 0
                          }
                          onChange={() => toggleSelectAll(group)}
                        />
                        <h4
                          style={{
                            marginBottom: 8,
                            fontSize: "16px",
                            fontWeight: 500,
                          }}>
                          {group.label.title}
                        </h4>
                      </div>
                    )}

                    {group.options.length > 0 ? (
                      <Controller
                        name={`milestones.${currentMilestoneIndex}.paymentMethods.${group.id}`}
                        control={control}
                        render={({ field }) => (
                          <div
                            style={{
                              display: "flex",
                              gap: "30px",
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}>
                            {group.options.map((opt) => (
                              <label
                                key={opt.label}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}>
                                <input
                                  type="checkbox"
                                  value={opt.value}
                                  disabled={
                                    !organizationMethods?.[
                                      group.label.value
                                    ]?.includes(opt.value)
                                  }
                                  className={styles.paymentCheckBox}
                                  checked={field.value?.includes(opt.value)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const newValue = checked
                                      ? [...field.value, opt.value]
                                      : field.value.filter(
                                          (v) => v !== opt.value
                                        );
                                    field.onChange(newValue);
                                  }}
                                />
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "14px",
                                    lineHeight: "22px",
                                    fontWeight: 500,
                                    color: field.value?.includes(opt.value)
                                      ? "#2C333C"
                                      : "#555B6A",
                                    textDecoration: !organizationMethods?.[
                                      group.label.value
                                    ]?.includes(opt.value)
                                      ? "line-through"
                                      : "",
                                  }}>
                                  {opt.label}
                                </p>
                              </label>
                            ))}
                          </div>
                        )}
                      />
                    ) : (
                      <Controller
                        name={`milestones.${currentMilestoneIndex}.paymentMethods.${group.id}`}
                        control={control}
                        render={({ field }) => (
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}>
                            <input
                              disabled={
                                group?.label?.value
                                  ? !organizationMethods?.[
                                      group?.label?.value ===
                                      EPaymentBrand.BANK_TRANSFER
                                        ? EPaymentType.MANUAL_BANK_TRANSFER
                                        : group?.label?.value
                                    ] ||
                                    organizationMethods?.[
                                      group?.label?.value ===
                                      EPaymentBrand.BANK_TRANSFER
                                        ? EPaymentType.MANUAL_BANK_TRANSFER
                                        : group?.label?.value
                                    ]?.length === 0
                                  : true
                              }
                              type="checkbox"
                              className={styles.paymentCheckBox}
                              checked={field.value?.includes(group.label.value)}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.checked ? [group.label.value] : []
                                );
                              }}
                            />
                            <p
                              style={{
                                margin: 0,
                                fontSize: "14px",
                                lineHeight: "22px",
                                fontWeight: 500,
                                color: field.value?.includes(group.label.value)
                                  ? "#2C333C"
                                  : "#555B6A",
                                textDecoration:
                                  !organizationMethods?.[
                                    group?.label?.value ===
                                    EPaymentBrand.BANK_TRANSFER
                                      ? EPaymentType.MANUAL_BANK_TRANSFER
                                      : group?.label?.value
                                  ] ||
                                  organizationMethods?.[
                                    group?.label?.value ===
                                    EPaymentBrand.BANK_TRANSFER
                                      ? EPaymentType.MANUAL_BANK_TRANSFER
                                      : group?.label?.value
                                  ]?.length === 0
                                    ? "line-through"
                                    : "",
                              }}>
                              {group.label.title}
                            </p>
                          </label>
                        )}
                      />
                    )}
                  </div>
                ))}

                {errors?.milestones?.[currentMilestoneIndex]
                  ?.paymentMethods && (
                  <p
                    style={{
                      color: "#eb4c43",
                      fontSize: "11.5px",
                    }}>
                    {errors?.milestones?.[
                      currentMilestoneIndex
                    ]?.paymentMethods?.message?.toString()}
                  </p>
                )}
              </>
            )}
          />
        </div>
      )}

      {isSourceDestinationEnabled && (
        <>
          <div className={styles.box}>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                className={styles.paymentCheckBox}
                checked={
                  !!watch(`milestones.${currentMilestoneIndex}.buyerOnly`)
                }
                onChange={(e) =>
                  setValue(
                    `milestones.${currentMilestoneIndex}.buyerOnly`,
                    e.target.checked
                  )
                }
              />
              <div>
                <h4 className={styles.toggleLabel}>
                  {t("lbl_buyers_only_contract")}
                </h4>
                <p className={styles.toggleHint}>
                  {t("lbl_buyers_only_contract_hint")}
                </p>
              </div>
            </label>
          </div>

          <div className={styles.box}>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                className={styles.paymentCheckBox}
                checked={isDestination}
                onChange={(e) => {
                  setValue(
                    `milestones.${currentMilestoneIndex}.isDestination`,
                    e.target.checked
                  );
                  if (!e.target.checked) {
                    setValue(
                      `milestones.${currentMilestoneIndex}.sourceContractId`,
                      ""
                    );
                  }
                }}
              />
              <div>
                <h4 className={styles.toggleLabel}>
                  {t("lbl_destination_contract")}
                </h4>
                <p className={styles.toggleHint}>
                  {t("lbl_destination_contract_hint")}
                </p>
              </div>
            </label>
            {isDestination && (
              <div className={styles.sourceInput}>
                <TextInput
                  label={t("lbl_source_contract_id")}
                  required
                  placeholder={t("lbl_source_contract_id_placeholder")}
                  value={
                    watch(
                      `milestones.${currentMilestoneIndex}.sourceContractId`
                    ) || ""
                  }
                  onChange={(e) => {
                    setValue(
                      `milestones.${currentMilestoneIndex}.sourceContractId`,
                      e.target.value
                    );
                  }}
                  errorMessage={
                    errors?.milestones?.[currentMilestoneIndex]
                      ?.sourceContractId?.message as string | undefined
                  }
                />
              </div>
            )}
          </div>
        </>
      )}

      <div className={styles.uploader}>
        <FileUploadManager
          errorMessage={errors?.milestones?.[
            currentMilestoneIndex
          ]?.images?.message?.toString()}
          onFilesChange={(files) => {
            setValue(`milestones.${currentMilestoneIndex}.images`, files);
          }}
          onUploadStatusChange={(uploading) => setIsUploading(uploading)}
          initialFiles={
            watch(`milestones.${currentMilestoneIndex}.images`) || []
          }
          ref={fileUploadref}
        />
      </div>

      <MilestoneFormNavButton
        disabled={!isValid || loading}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSaveDraft={handleSaveDraft}
      />

      <Modal
        customModalClassName={styles.modal}
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false);
        }}>
        <div className={styles.tiersContainer}>
          <TiersInformation organization={organization} />
          <OutlinedButton
            onClick={() => setModalOpen(false)}
            style={{
              width: "331px",
              color: "#0D44AD",
              background: "#F3F6FC",
              border: "none",
            }}>
            {t("closed")}
          </OutlinedButton>
        </div>
      </Modal>
    </div>
  );
};

export default StepTwo;
